import React from 'react';

/**
 * DashBoardDialog — 대시보드 공통 다이얼로그 컴포넌트 (Tailwind)
 *
 * ─────────────────────────────────────────────────────────────
 * field 객체 shape:
 * {
 *   label    : string,                         // 필드 상단 라벨
 *   value    : node,                           // 읽기전용 박스 안 내용 (string | ReactNode)
 *   span     : 1 | 2,                          // 컬럼 수 (1=절반, 2=전체행). 기본값 1
 *   tall     : boolean,                        // true면 박스 최소 높이 증가 (textarea 스타일)
 *   boxStyle : 'filled'|'outlined'|'ghost',    // 필드 개별 박스 스타일 (미지정 시 fieldBoxStyle 전역값 사용)
 * }
 * ─────────────────────────────────────────────────────────────
 *
 * @param {boolean}               open          - 다이얼로그 열림 여부
 * @param {function}              onClose       - 닫기 콜백
 * @param {string|node}           title         - 헤더 타이틀
 * @param {node}                  titleIcon     - 타이틀 좌측 아이콘 영역 (optional)
 * @param {Array<FieldObj>}       fields        - 필드 목록
 * @param {node}                  headerExtra   - 필드 위에 삽입할 추가 영역 (탭 등)
 * @param {node}                  actions       - 푸터 버튼 영역
 * @param {'sm'|'md'|'lg'|'xl'}   size          - 다이얼로그 너비 (기본 'md')
 * @param {'filled'|'outlined'|'ghost'} fieldBoxStyle - 필드 박스 스타일 (기본 'filled')
 *   - 'filled'   : 배경색 O + 보더 O  (bg-gray-50 + border)
 *   - 'outlined' : 배경색 X + 보더 O  (transparent + border)
 *   - 'ghost'    : 배경색 X + 보더 X  (스타일 없음)
 */
function DashBoardDialog({
  open = false,
  onClose = () => {},
  title = '',
  titleIcon = null,
  fields = [],
  headerExtra = null,
  actions = null,
  size = 'md',
  fieldBoxStyle = 'filled', // 'filled' | 'outlined' | 'ghost'
}) {
  if (!open) return null;

  const sizeClass =
    {
      sm: 'max-w-sm',
      md: 'max-w-lg',
      lg: 'max-w-2xl',
      xl: 'max-w-4xl',
    }[size] ?? 'max-w-lg';

  return (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center p-4"
      style={{
        backgroundColor: 'rgba(0,0,0,0.4)',
        backdropFilter: 'blur(2px)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`bg-white w-full ${sizeClass} rounded-2xl shadow-[0_20px_40px_rgba(25,28,30,0.15)] flex flex-col overflow-hidden`}
      >
        {/* ── 헤더 ── */}
        <div className="px-6 py-4 border-b border-[#e1e8ed] flex items-center justify-between bg-white shrink-0">
          <div className="flex items-center gap-3">
            {titleIcon && titleIcon}
            {typeof title === 'string' ? (
              <h3 className="text-base font-bold text-[#2c3e50]">{title}</h3>
            ) : (
              title
            )}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-[#7f8c8d] hover:bg-gray-100 hover:text-[#2c3e50] transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* ── 콘텐츠 ── */}
        <div className="px-6 py-5 flex-1 overflow-y-auto">
          {headerExtra && <div className="mb-4">{headerExtra}</div>}

          <div className="grid grid-cols-2 gap-x-4 gap-y-4">
            {fields.map((field, idx) => (
              <div
                key={idx}
                className={field.span === 2 ? 'col-span-2' : 'col-span-1'}
              >
                {field.label && (
                  <label className="block text-[11px] font-bold text-[#7f8c8d] uppercase tracking-wider mb-1">
                    {field.label}
                  </label>
                )}
                <div
                  className={`
                  rounded-lg px-3 py-2.5 text-sm font-semibold text-[#2c3e50] flex items-center
                  ${{ filled: 'bg-gray-50 border border-[#e1e8ed]', onlyBg: 'bg-gray-50', ghost: '' }[field.boxStyle ?? fieldBoxStyle] ?? 'bg-gray-50 border border-[#e1e8ed]'}
                  ${field.tall ? 'min-h-20 items-start!' : 'min-h-10'}
                `}
                >
                  {typeof field.value === 'string' ? (
                    <span
                      className={!field.value ? 'text-gray-400 italic' : ''}
                    >
                      {field.value || '-'}
                    </span>
                  ) : (
                    field.value
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── 푸터 ── */}
        {actions && (
          <div className="px-6 py-4 border-t border-[#e1e8ed] bg-gray-50/50 shrink-0">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

export default DashBoardDialog;
